import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import WaveChart from '../components/charts/WaveChart'
import TopMoversSection from '../components/stocks/TopMoversSection'
import { transformCSVToWaveData, getMarketTrend, CSVWeeklyData } from '../utils/dataTransformers'
import EmailCapture from '../components/EmailCapture'

const HomePage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)

  // Load real CSV data
  const { data: marketData, isLoading, error } = useQuery({
    queryKey: ['marketData'],
    queryFn: async () => {
      try {
        // Load the CSV data
        const response = await fetch('/data/weekly_summary.json')
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        const result = { data }
        
        // Load top movers data
        const moversResponse = await fetch('/data/weekly_top_movers.json')
        if (!moversResponse.ok) {
          throw new Error(`Failed to fetch movers data: ${moversResponse.status} ${moversResponse.statusText}`)
        }
        const moversData = await moversResponse.json()
        const moversResult = { data: moversData }
        
        // Transform CSV data to wave format - show weeks 0-5 for 6 weeks total
        const allWeeks = result.data as CSVWeeklyData[]
        const next6Weeks = allWeeks.slice(0, 6) // Take weeks 0-5 (6 weeks total)
        const waveData = transformCSVToWaveData(next6Weeks)
        const trend = getMarketTrend(waveData)
        
        // Get current week data
        const currentWeek = result.data[0] as {
          Bullish_Percent: number
          Market_Signal: string
          Total_Stocks: number
        }
        const currentWeekMovers = moversResult.data[0] as {
          Top_Bullish_Symbol: string
          Top_Bearish_Symbol: string
          Top_Bullish_Delta: number
          Top_Bearish_Delta: number
          Top_Bullish_Dollar_Delta: number
          Top_Bearish_Dollar_Delta: number
          Top_Bullish_Price: number
          Top_Bearish_Price: number
        }
        
        return {
          waveData,
          currentSentiment: {
            bullishPercent: currentWeek.Bullish_Percent,
            bearishPercent: 100 - currentWeek.Bullish_Percent,
            signal: currentWeek.Market_Signal,
            totalStocks: currentWeek.Total_Stocks
          },
          trend,
          topMover: currentWeekMovers ? {
            symbol: currentWeek.Bullish_Percent >= 50 
              ? currentWeekMovers.Top_Bullish_Symbol 
              : currentWeekMovers.Top_Bearish_Symbol,
            delta: currentWeek.Bullish_Percent >= 50 
              ? currentWeekMovers.Top_Bullish_Delta 
              : currentWeekMovers.Top_Bearish_Delta,
            dollarDelta: currentWeek.Bullish_Percent >= 50 
              ? currentWeekMovers.Top_Bullish_Dollar_Delta 
              : currentWeekMovers.Top_Bearish_Dollar_Delta,
            price: currentWeek.Bullish_Percent >= 50 
              ? currentWeekMovers.Top_Bullish_Price 
              : currentWeekMovers.Top_Bearish_Price,
            isBullish: currentWeek.Bullish_Percent >= 50
          } : null
        }
      } catch (error) {
        console.error('Error loading market data:', error)
        // Fallback to mock data
        return {
          waveData: [],
          currentSentiment: {
            bullishPercent: 52,
            bearishPercent: 48,
            signal: 'Bullish',
            totalStocks: 500
          },
          trend: 'stable',
          topMover: null
        }
      }
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours since data updates daily
  })

  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Display error if there is one
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Data</h1>
          <p className="text-gray-600 mb-4">{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
          <p className="text-sm text-gray-500">Please check the console for more details.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-primary-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`
            text-center transition-all duration-1000 transform
            ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}
          `}>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              Ride the{' '}
              <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                StockSines
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
              Visualize the aggregate options flow from all 500 S&P 500 stocks. 
              Our proprietary analysis combines individual stock options to reveal net market direction.
            </p>
            
            {/* Current Market State */}
            {marketData && (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 max-w-md mx-auto mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Current Market State</h3>
                <div className="flex justify-center items-center space-x-4">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      marketData.currentSentiment.signal === 'Bullish' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {marketData.currentSentiment.bullishPercent.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Bullish</div>
                  </div>
                  <div className="text-gray-400">|</div>
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-gray-700">
                      {marketData.currentSentiment.signal}
                    </div>
                    <div className="text-sm text-gray-600">Signal</div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Aggregate analysis from {marketData.currentSentiment.totalStocks} individual S&P 500 stock options
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#wave-preview"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-all hover:scale-105"
              >
                View Market Forecast
                <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Live Market Wave Preview */}
      <section id="wave-preview" className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              6-Week S&P 500 Components Options Flow
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Aggregated options flow from all 500 individual stocks revealing net market positioning
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6">
            {isLoading ? (
              <div className="flex justify-center items-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : marketData && marketData.waveData.length > 0 ? (
              <>
                <WaveChart
                  data={marketData.waveData}
                  height={400}
                  animated={true}
                  showTooltips={true}
                />
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Future market trend: <span className="font-semibold capitalize">{marketData.trend}</span>
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Unable to load market data. Please try again later.
              </div>
            )}
          </div>

          {/* Top Movers Display */}
          <div className="mt-8">
            <TopMoversSection weekOffset={0} />
          </div>

          {/* Free vs Premium Comparison */}
          <div className="mt-8 grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Free Access</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700">View market sentiment waves</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700">See weekly top bullish/bearish stocks</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700">Daily market updates</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg shadow-md p-6 border-2 border-primary-200 relative overflow-hidden">
              <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                COMING SOON
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Premium Access
                <span className="ml-2 text-sm font-normal text-primary-600">$9.99/month</span>
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700 font-medium">All 500+ stocks ranked by signal strength</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700 font-medium">Detailed strength indicators</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-primary-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-gray-700 font-medium">Export data for analysis</span>
                </li>
              </ul>
              <div className="border-t border-primary-200 pt-4">
                <p className="text-sm text-gray-700 mb-3 font-medium">Get notified when Premium launches:</p>
                <EmailCapture source="homepage-premium-box" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="py-10 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to See All 500+ Stocks?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Premium members get access to detailed delta rankings for every S&P 500 stock, 
            strength indicators, and data export capabilities.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 max-w-md mx-auto">
            <div className="bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-2 rounded-full inline-block mb-4">
              LAUNCHING SOON
            </div>
            <p className="text-white mb-4">
              Be among the first to get Premium access when we launch.
            </p>
            <EmailCapture source="homepage-cta-section" className="text-left" />
          </div>
        </div>
      </section>

      {/* Thesis Section */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">
              The StockSines Thesis
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Why aggregated options flow reveals true market direction
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 space-y-5">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The Signal in the Noise</h3>
              <p className="text-gray-700 leading-relaxed">
                While individual stock movements can be erratic and influenced by company-specific news, 
                the aggregate options positioning across all 500 S&P 500 components reveals a clearer picture. 
                When institutional traders place bets across the entire market, patterns emerge.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Options Flow: The Hidden Indicator</h3>
              <p className="text-gray-700 leading-relaxed">
                Options flow analysis reveals how institutional traders are positioning themselves. 
                By analyzing thousands of option contracts for every S&P 500 stock, we capture 
                the net directional exposure of market participants. Our proprietary calculations 
                transform this raw data into clear bullish or bearish signals.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The 6-Week Window</h3>
              <p className="text-gray-700 leading-relaxed">
                Weekly options provide a unique 6-week forward view into market expectations. Unlike daily 
                price movements driven by algorithms and retail traders, weekly options positioning represents 
                deliberate bets by institutions managing billions. Our analysis aggregates these positions 
                to project the sine wave of market sentiment.
              </p>
            </div>
            
            <div className="border-t pt-6">
              <p className="text-sm text-gray-600 italic">
                Note: This analysis combines publicly available options data using proprietary calculations. 
                The specific methodology and weighting algorithms remain confidential to protect our edge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Powered by Advanced Analytics
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Our proprietary algorithms analyze options data to predict market movements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">500 Stock Aggregate Analysis</h3>
              <p className="text-gray-600">
                Combines options data from every S&P 500 stock into one unified signal
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Net Market Direction</h3>
              <p className="text-gray-600">
                6-week projection based on cumulative options positioning across all 500 stocks
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Hidden Edge Discovery</h3>
              <p className="text-gray-600">
                Uncover market opportunities others miss with our proprietary options flow analysis
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage